import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SEO from '../components/SEO';
import { 
  ArrowLeft, Search, Book, PlayCircle, Settings, Users, Package, Truck, 
  CreditCard, MessageSquare, Calendar, QrCode, MapPin, FileText, Shield,
  Smartphone, Building2, Calculator, Clock, Bell, Camera, ChevronRight,
  Star, CheckCircle
} from 'lucide-react';
import { search as searchDocs } from './docs/docsSearch';

const DocumentationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      title: "Getting Started",
      icon: PlayCircle,
      color: "bg-green-500",
      docs: [
        { title: "Quick Start Guide", description: "Get up and running in minutes", link: "/docs/quick-start", popular: true },
        { title: "Creating Your Company Account", description: "Step-by-step registration guide", link: "/docs/registration" },
        { title: "Dashboard Overview", description: "Navigate the control panel", link: "/docs/dashboard" },
        { title: "Adding Your First Job", description: "Create and manage moving jobs", link: "/docs/first-job" },
      ]
    },
    {
      title: "Job Management",
      icon: Truck,
      color: "bg-blue-500",
      docs: [
        { title: "Creating Jobs", description: "Full guide to job creation", link: "/docs/creating-jobs", popular: true },
        { title: "Job Statuses & Workflow", description: "Understanding the job lifecycle", link: "/docs/job-workflow" },
        { title: "Assigning Teams to Jobs", description: "Manage crew assignments", link: "/docs/team-assignments" },
        { title: "Job Timeline & History", description: "Track all job activities", link: "/docs/job-history" },
      ]
    },
    {
      title: "Inventory & QR Codes",
      icon: QrCode,
      color: "bg-purple-500",
      docs: [
        { title: "AI Inventory Scanning", description: "Use AI to catalogue items", link: "/docs/ai-scanning", popular: true },
        { title: "QR Code System", description: "Print, assign, and track items", link: "/docs/qr-codes" },
        { title: "Inventory Templates", description: "Create reusable item templates", link: "/docs/inventory-templates" },
        { title: "Scan Console", description: "Loading/unloading verification", link: "/docs/scan-console" },
      ]
    },
    {
      title: "Estimates & Billing",
      icon: Calculator,
      color: "bg-amber-500",
      docs: [
        { title: "Creating Estimates", description: "Generate professional quotes", link: "/docs/estimates", popular: true },
        { title: "Pricing Configuration", description: "Set up rates and charges", link: "/docs/pricing-setup" },
        { title: "Distance-Based Pricing", description: "Auto-calculate travel costs", link: "/docs/distance-pricing" },
        { title: "Storage Pricing", description: "Configure storage rates", link: "/docs/storage-pricing" },
        { title: "Payment Methods", description: "Accept payments from customers", link: "/docs/payments" },
      ]
    },
    {
      title: "Team Management",
      icon: Users,
      color: "bg-indigo-500",
      docs: [
        { title: "Adding Team Members", description: "Invite drivers, porters & staff", link: "/docs/adding-users" },
        { title: "Roles & Permissions", description: "Understand user access levels", link: "/docs/roles" },
        { title: "Driver Tracking", description: "Real-time GPS location", link: "/docs/driver-tracking" },
        { title: "Time Tracking", description: "Track work hours", link: "/docs/time-tracking" },
      ]
    },
    {
      title: "Customer Communication",
      icon: MessageSquare,
      color: "bg-pink-500",
      docs: [
        { title: "Chat System", description: "Message customers & team", link: "/docs/chat" },
        { title: "Notification Templates", description: "Customize email/SMS alerts", link: "/docs/notifications" },
        { title: "Customer Portal", description: "What customers can do", link: "/docs/customer-portal" },
      ]
    },
    {
      title: "Storage Management",
      icon: Building2,
      color: "bg-teal-500",
      docs: [
        { title: "Storage Units", description: "Manage storage facilities", link: "/docs/storage-units" },
        { title: "Storage Pricing Models", description: "Per-box vs per-volume pricing", link: "/docs/storage-models" },
        { title: "Storage Handling Charges", description: "Set loading/unloading fees", link: "/docs/storage-handling" },
      ]
    },
    {
      title: "Integrations",
      icon: Calendar,
      color: "bg-cyan-500",
      docs: [
        { title: "Google Calendar", description: "Sync jobs to your calendar", link: "/docs/google-calendar" },
        { title: "Google Maps", description: "Address autocomplete & routing", link: "/docs/google-maps" },
        { title: "Payment Processors", description: "Stripe integration guide", link: "/docs/stripe" },
      ]
    },
    {
      title: "Mobile Apps",
      icon: Smartphone,
      color: "bg-orange-500",
      docs: [
        { title: "Mobile App Overview", description: "Apps for drivers & customers", link: "/docs/mobile-overview" },
        { title: "Driver App Features", description: "Navigation, scanning, tracking", link: "/docs/driver-app" },
        { title: "Customer App Features", description: "Booking, tracking, chat", link: "/docs/customer-app" },
        { title: "Installing the APK", description: "Android installation guide", link: "/docs/apk-install" },
      ]
    },
    {
      title: "Settings & Configuration",
      icon: Settings,
      color: "bg-slate-500",
      docs: [
        { title: "Company Settings", description: "Configure your company profile", link: "/docs/company-settings" },
        { title: "White-Label Branding", description: "Customize with your brand", link: "/docs/white-label" },
        { title: "Estimate Settings", description: "Configure all pricing options", link: "/docs/estimate-settings" },
        { title: "Notification Settings", description: "Control alerts & reminders", link: "/docs/notification-settings" },
      ]
    },
  ];

  // Full-text search hits across every doc page (memoized)
  const fullTextHits = useMemo(
    () => (searchQuery.trim() ? searchDocs(searchQuery, 25) : []),
    [searchQuery]
  );

  /**
   * Fire a GA4 `docs_search` event 800ms after the user stops typing.
   * Captures both the query and how many results it produced so you can
   * see zero-result queries (real product research signal) in GA4 →
   * Admin → Events.
   */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) return; // ignore 1-char noise
    const t = setTimeout(() => {
      if (typeof window.gtag === 'function') {
        try {
          window.gtag('event', 'docs_search', {
            query: q.toLowerCase(),
            result_count: fullTextHits.length,
            has_results: fullTextHits.length > 0,
          });
        } catch (e) { /* ignore */ }
      }
    }, 800);
    return () => clearTimeout(t);
  }, [searchQuery, fullTextHits.length]);

  // Highlight-safe snippet renderer — wraps matches in <mark>.
  const highlight = (text, q) => {
    if (!text || !q) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${safe})`, 'ig'));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase()
        ? <mark key={i} className="bg-amber-200 text-gray-900 px-0.5 rounded-sm">{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  };

  // Filter categories based on search (still used when search is empty)
  const filteredCategories = categories.map(cat => ({
    ...cat,
    docs: cat.docs.filter(doc => 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.docs.length > 0 || searchQuery === '');

  // Get popular docs
  const popularDocs = categories.flatMap(cat => 
    cat.docs.filter(doc => doc.popular).map(doc => ({ ...doc, category: cat.title, icon: cat.icon, color: cat.color }))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Documentation - MoveMate"
        description="Learn how to use MoveMate - comprehensive guides, tutorials, and documentation for the complete moving management platform."
        url="https://movemate.tech/docs"
      />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Book size={40} />
            <h1 className="text-3xl font-bold">Documentation</h1>
          </div>
          <p className="text-cyan-100 text-lg mb-8 max-w-2xl">
            Everything you need to know about using MoveMate. From getting started to advanced features.
          </p>
          
          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input 
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg bg-white text-gray-900 border-0"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto py-12 px-4">
        
        {/* Popular Docs */}
        {searchQuery === '' && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Star className="text-amber-500" size={24} />
              Popular Guides
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {popularDocs.map((doc, idx) => (
                <Link key={idx} to={doc.link}>
                  <Card className="hover:shadow-lg transition-shadow h-full cursor-pointer group">
                    <CardContent className="p-4">
                      <div className={`w-10 h-10 rounded-lg ${doc.color} text-white flex items-center justify-center mb-3`}>
                        <doc.icon size={20} />
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* All Categories / Search Results */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {searchQuery
              ? <>Search Results for "<span className="text-primary">{searchQuery}</span>" <span className="text-base font-normal text-gray-500">({fullTextHits.length} {fullTextHits.length === 1 ? 'match' : 'matches'})</span></>
              : 'All Documentation'}
          </h2>

          {/* Search active: render full-text hits */}
          {searchQuery ? (
            fullTextHits.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm" data-testid="no-results">
                <p className="text-gray-500 text-lg mb-2">No documentation found matching "{searchQuery}".</p>
                <p className="text-gray-400 text-sm mb-4">Try a different keyword, or contact support.</p>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={() => setSearchQuery('')} data-testid="clear-search-btn">Clear Search</Button>
                  <Link to="/contact"><Button>Contact Support</Button></Link>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="search-results-list">
                {fullTextHits.map((hit, i) => (
                  <Link
                    key={hit.slug}
                    to={hit.url}
                    className="block bg-white rounded-lg p-5 border hover:border-primary hover:shadow-md transition-all group"
                    data-testid={`search-hit-${hit.slug}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                            {highlight(hit.title, searchQuery)}
                          </h3>
                          <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                            {hit.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {highlight(hit.snippet || hit.description, searchQuery)}
                        </p>
                      </div>
                      <ChevronRight size={20} className="text-gray-400 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
                    </div>
                  </Link>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-8">
              {filteredCategories.map((category, catIdx) => (
                <Card key={catIdx}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${category.color} text-white flex items-center justify-center`}>
                        <category.icon size={20} />
                      </div>
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {category.docs.map((doc, docIdx) => (
                        <Link 
                          key={docIdx} 
                          to={doc.link}
                          className="flex items-center justify-between p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all group"
                        >
                          <div>
                            <h4 className="font-medium text-gray-900 group-hover:text-primary transition-colors flex items-center gap-2">
                              {doc.title}
                              {doc.popular && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Popular</span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                          </div>
                          <ChevronRight size={20} className="text-gray-400 group-hover:text-primary transition-colors" />
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Help Section */}
        <section className="mt-12 bg-gradient-to-r from-primary/10 to-cyan-500/10 rounded-2xl p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Can't find what you're looking for?</h2>
            <p className="text-gray-600 mb-6">Our support team is here to help you with any questions.</p>
            <div className="flex justify-center gap-4">
              <Link to="/contact">
                <Button>Contact Support</Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline">View FAQ</Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary">Terms & Conditions</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </div>
          <p className="text-gray-400 text-sm mt-4">© 2026 MoveMate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DocumentationPage;
